import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import get from 'just-safe-get';
import ConversionField from '@/components/parameters/ConversionField';
import usePatchParams from '@/components/parameters/usePatchParams';
import { useEffect, useRef } from 'react';
import { isDefined } from '@/utils/miscUtils';
import { NumericFormatInput } from '@/components/core/form/NumericFormatInput';
import { useParamsContext } from '@/components/providers/useParamsContext';
import MCFieldBase from '@/components/parameters/fields/MCFieldBase';
import MCActiveField from './MCActiveField';
import AnnualChangeBase from '@/components/parameters/fields/AnnualChangeBase';
import AnnualChangeBeginning from '@/components/parameters/fields/AnnualChangeBeginning';
import { useShowTimeSeries } from '@/stores/panelStore';

const MonteCarloRow = ({ nodeId, param, paramSchema, name, label }) => {
  const showTimeSeries = useShowTimeSeries();
  const { convertible_units: convertibleUnits, editable } = paramSchema;
  const {
    primary: { value, is_default: isDefault, default: defaultValue } = {},
    mc_min,
    mc_max,
    annual_change,
    years,
    annual_change_beginning,
    uncertainty_active = { value: true },
    unit,
  } = param ?? {};
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const paramName = `${nodeId}.${name}`;
  const { readOnly } = useParamsContext();
  const isAuto = value?.auto === true;
  const disabled = !editable || readOnly || isAuto;
  const error = get(errors, paramName) || get(errors, `annual_change.${paramName}`);
  const errorMessage = error?.message;
  const { patchPrimary } = usePatchParams({ nodeId });
  const numericValueRef = useRef(null);

  const events = {
    onChange: e => {
      e.preventDefault();
    },
    onBlur: async e => {
      const prevValue = getValues(paramName);
      const value = numericValueRef.current ?? parseFloat(e.target.value.replace(/,/g, ''));

      if (prevValue !== value) {
        await patchPrimary(paramName, value);
      } else {
        setValue(paramName, value, { shouldValidate: true, shouldDirty: false });
      }
    },
  };

  useEffect(() => {
    if (isDefined(value) && !isAuto) {
      setValue(paramName, value, { shouldValidate: true, shouldDirty: false });
    }
  }, [value, paramName, setValue, isAuto]);

  return (
    <>
      <tr className="border-b border-ivory-100">
        <td className="text-left py-2 text-sm pl-10 pr-4">
          <div className="flex items-center space-x-2">
            <MCActiveField
              disabled={disabled}
              param={uncertainty_active}
              className="checkbox-xs [--chkbg:#0066ff] [--fallback-inc:white]"
              name={paramName}
              options={{ nodeId }}
            />
            <span>{label}</span>
          </div>
        </td>
        <td className="text-center py-2 px-4">
          <MCFieldBase
            disabled={disabled}
            param={mc_min}
            className="w-28 mx-auto"
            type="mc_min"
            name={paramName}
            options={{ nodeId }}
          />
        </td>
        <td className="text-center py-2 px-4">
          <NumericFormatInput
            id={paramName}
            name={paramName}
            disabled={disabled}
            readOnly={readOnly}
            control={control}
            {...events}
            placeholder={defaultValue?.toString()}
            isDefault={isDefault}
            onNumericChange={value => {
              numericValueRef.current = value;
            }}
            className="w-28 mx-auto"
          />
        </td>
        <td className="text-center py-2 px-4">
          <MCFieldBase
            disabled={disabled}
            param={mc_max}
            className="w-28 mx-auto"
            type="mc_max"
            name={paramName}
            options={{ nodeId }}
          />
        </td>
        <td className="text-center py-2">
          {convertibleUnits?.length > 0 && (
            <ConversionField
              unit={unit}
              name={`conversion.${paramName}`}
              paramName={paramName}
              className="w-48 mx-auto"
              units={convertibleUnits}
              disabled={disabled}
              readOnly={readOnly}
              options={{ nodeId }}
            />
          )}
        </td>
        {showTimeSeries && (
          <>
            <td className="text-center py-2">
              {annual_change && (
                <AnnualChangeBase
                  name={paramName}
                  disabled={disabled}
                  className="w-28 mx-auto"
                  param={annual_change}
                  options={{ nodeId }}
                />
              )}
            </td>
            <td className="text-center py-2">
              {annual_change_beginning && (
                <AnnualChangeBeginning
                  name={paramName}
                  disabled={disabled}
                  className="w-28 mx-auto"
                  param={annual_change_beginning}
                  options={{ nodeId }}
                  selectOptions={years}
                />
              )}
            </td>
          </>
        )}
      </tr>
      <tr>
        <td colSpan={6} className="text-center py-2 text-red-500 text-sm">
          {errorMessage}
        </td>
      </tr>
    </>
  );
};

MonteCarloRow.propTypes = {
  nodeId: PropTypes.string,
  param: PropTypes.object,
  paramSchema: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  children: PropTypes.node,
};

export default MonteCarloRow;
